import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Briefcase, MapPin, SearchX } from "lucide-react";
import { get } from "@/lib/api";
import { Skel } from "@/components/app/Common";
import { Stagger, StaggerItem } from "@/components/motion/Motion";
import { PageTour } from "@/components/app/PageTour";

export function CareersFrame({ brand, children, wide }) {
  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-line bg-white">
        <div className={`mx-auto flex items-center gap-3 px-5 py-4 ${wide ? "max-w-[960px]" : "max-w-[760px]"}`}>
          {brand?.logo_data_url && <img src={brand.logo_data_url} alt="" className="h-8 w-8 rounded object-contain" />}
          <Link to="/careers" className="font-display text-lg font-semibold" style={{ color: brand?.primary_color || "#1F4D3A" }} data-testid="careers-company-name">{brand?.company_name || "Careers"}</Link>
        </div>
      <div className="mx-auto flex max-w-[760px] justify-end px-6 pb-2"><PageTour /></div>
      </header>
      <main className={`tl-page mx-auto space-y-6 px-5 py-8 sm:py-12 ${wide ? "max-w-[960px]" : "max-w-[760px]"}`}>{children}</main>
    </div>
  );
}

export const SeeOpenRoles = ({ testId = "see-open-roles" }) => (
  <Link to="/careers" data-testid={testId} className="inline-flex items-center gap-1.5 rounded-[5px] bg-brand px-4 py-2 text-sm font-medium text-white transition-[transform,background-color,box-shadow] duration-150 hover:-translate-y-px hover:bg-brand-hover hover:shadow-[0_0_24px_rgba(31,77,58,0.5)] active:scale-[0.97]">
    See open roles <ArrowRight size={16} aria-hidden="true" />
  </Link>
);

export const RoleNotFound = () => (
  <div className="panel space-y-3 p-8" data-testid="careers-role-not-found">
    <SearchX size={28} className="text-ink-muted" aria-hidden="true" />
    <h1 className="text-2xl font-semibold">Role not found</h1>
    <p className="text-ink-secondary">We couldn't find this job. The link may be mistyped or out of date.</p>
    <SeeOpenRoles />
  </div>
);

export const NotAccepting = () => (
  <div className="panel space-y-3 p-8" data-testid="careers-not-found">
    <h1 className="text-2xl font-semibold">This job isn't accepting applications</h1>
    <p className="text-ink-secondary">It has been filled, paused or closed. Thanks for your interest.</p>
    <SeeOpenRoles />
  </div>
);

export default function CareersIndex() {
  const { data, isLoading } = useQuery({ queryKey: ["careers-index"], queryFn: () => get("/public/careers") });
  return (
    <CareersFrame brand={data} wide>
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl" data-tour="pt-careers-index-title" data-testid="careers-index-title">Open roles</h1>
        <p className="text-ink-secondary">{data ? `${data.jobs.length} open role${data.jobs.length === 1 ? "" : "s"} at ${data.company_name}` : "Finding open roles"}</p>
      </div>
      {isLoading ? <div className="space-y-3">{[0, 1, 2].map((i) => <Skel key={i} className="h-24" />)}</div> : !data.jobs.length ? (
        <div className="panel p-8 text-ink-secondary" data-testid="careers-index-empty">There are no open roles right now. Please check back soon.</div>
      ) : (
        <Stagger as="ul" className="space-y-3" data-tour="pt-careers-index-list" data-testid="careers-index-list">
          {data.jobs.map((j) => (
            <StaggerItem as="li" key={j.id}>
              <Link to={`/careers/${j.slug}`} data-testid={`careers-index-job-${j.slug}`} className="tl-lift tl-row panel flex items-center gap-4 p-5">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-display text-lg font-semibold text-ink">{j.title}</p>
                  <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
                    {j.location && <span className="inline-flex items-center gap-1"><MapPin size={14} aria-hidden="true" /> {j.location}</span>}
                    {[j.practice_area, j.law_firm, j.pqe].filter(Boolean).length > 0 && <span className="inline-flex items-center gap-1" data-testid={`careers-index-meta-${j.slug}`}><Briefcase size={14} aria-hidden="true" /> {[j.practice_area, j.law_firm, j.pqe].filter(Boolean).join(" · ")}</span>}
                    {(j.department || j.employment_type) && <span className="inline-flex items-center gap-1"><Briefcase size={14} aria-hidden="true" /> {[j.department, j.employment_type].filter(Boolean).join(" · ")}</span>}
                  </p>
                  {j.summary && <p className="line-clamp-2 text-sm text-ink-secondary">{j.summary}</p>}
                </div>
                <ArrowRight size={18} className="tl-chev text-brand" aria-hidden="true" />
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </CareersFrame>
  );
}
